import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listSymbols, watchlistAdd, watchlistRemove, getWatchlist } from "@/lib/api";
import { friendlyName } from "@/lib/symbols";
import { Star, Search as SearchIcon, X } from "lucide-react";

const CLASS_LABELS = {
  all: "ALL",
  future: "COMMODITIES",
  fx: "FOREX",
  crypto: "CRYPTOS",
  stock: "SHARES",
  etf: "ETF",
};

const CLASS_ORDER = ["all", "future", "fx", "crypto", "stock", "etf"];

function fuzzyMatch(q, row) {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    row.symbol.toLowerCase().includes(needle) ||
    (row.name || "").toLowerCase().includes(needle)
  );
}

export default function SymbolSearchModal({ open, onClose, onSelect }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("all");
  const [watch, setWatch] = useState(new Set());
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    listSymbols().then((r) => setItems(r.items || [])).catch(() => setItems([]));
    getWatchlist().then((r) => setWatch(new Set(r.symbols || []))).catch(() => {});
    setTimeout(() => inputRef.current?.focus(), 40);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    const rows = items.filter((r) => {
      if (cls !== "all" && r.asset_class !== cls) return false;
      return fuzzyMatch(q, r);
    });
    // Rank exact prefix matches first
    if (q) {
      const needle = q.toLowerCase();
      rows.sort((a, b) => {
        const aStart = a.symbol.toLowerCase().startsWith(needle) ? 0 : 1;
        const bStart = b.symbol.toLowerCase().startsWith(needle) ? 0 : 1;
        return aStart - bStart;
      });
    }
    return rows.slice(0, 200);
  }, [items, q, cls]);

  const toggleWatch = async (sym) => {
    if (watch.has(sym)) {
      setWatch((s) => { const n = new Set(s); n.delete(sym); return n; });
      try { await watchlistRemove(sym); } catch {}
    } else {
      setWatch((s) => new Set([...s, sym]));
      try { await watchlistAdd(sym); } catch {}
    }
  };

  const pick = (row) => {
    onClose?.();
    if (onSelect) onSelect(row.symbol);
    else nav(`/desk?symbol=${encodeURIComponent(row.symbol)}`);
  };

  if (!open) return null;
  return (
    <div
      data-testid="symbol-search-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="mt-16 w-full max-w-2xl overflow-hidden border border-[var(--aurion-border)] bg-[var(--aurion-panel)]/95 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-2 border-b border-[var(--aurion-border)] px-3 py-2">
          <SearchIcon className="h-4 w-4 text-[var(--aurion-muted)]" />
          <input
            ref={inputRef}
            data-testid="symbol-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by symbol or name (e.g. gold, EURUSD, AAPL)…"
            className="flex-1 bg-transparent text-sm text-[var(--aurion-text)] outline-none placeholder:text-[var(--aurion-muted)]"
          />
          <span className="kbd">esc</span>
          <button
            data-testid="symbol-search-close"
            onClick={onClose}
            className="ml-1 text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {/* Filter chips */}
        <div className="flex flex-wrap gap-1 border-b border-[var(--aurion-border)] px-3 py-2">
          {CLASS_ORDER.map((k) => (
            <button
              key={k}
              data-testid={`symbol-filter-${k}`}
              onClick={() => setCls(k)}
              className={`px-2 py-[3px] text-[9px] font-heading tracking-[0.18em] transition-colors border ${
                cls === k
                  ? "border-[var(--aurion-gold)] text-[var(--aurion-gold)] bg-[var(--aurion-gold)]/10"
                  : "border-[var(--aurion-border)] text-[var(--aurion-muted)] hover:text-[var(--aurion-text)]"
              }`}
            >
              {CLASS_LABELS[k]}
            </button>
          ))}
        </div>
        {/* Results */}
        <div
          className="max-h-[60vh] overflow-y-auto"
          data-testid="symbol-search-results"
        >
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-[var(--aurion-muted)]">
              No symbols match.
            </div>
          ) : (
            filtered.map((row) => (
              <div
                key={row.symbol}
                data-testid={`symbol-search-row-${row.symbol}`}
                onClick={() => pick(row)}
                className="group flex cursor-pointer items-center justify-between border-b border-[var(--aurion-border)]/40 px-3 py-2 hover:bg-white/[0.03]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <button
                    data-testid={`symbol-search-star-${row.symbol}`}
                    onClick={(e) => { e.stopPropagation(); toggleWatch(row.symbol); }}
                    className={`shrink-0 ${
                      watch.has(row.symbol)
                        ? "text-[var(--aurion-gold)]"
                        : "text-[var(--aurion-muted)] hover:text-[var(--aurion-gold)]"
                    }`}
                  >
                    <Star
                      className="h-3.5 w-3.5"
                      fill={watch.has(row.symbol) ? "currentColor" : "none"}
                    />
                  </button>
                  <span className="font-heading text-xs tracking-widest text-[var(--aurion-text)]">
                    {row.symbol}
                  </span>
                  <span className="truncate text-xs text-[var(--aurion-muted-strong)]">
                    {friendlyName(row.symbol, row.name)}
                  </span>
                </div>
                <span
                  className="rounded-sm border border-[var(--aurion-border)] px-1.5 py-[1px] text-[9px] font-heading tracking-[0.18em] text-[var(--aurion-muted)]"
                  data-testid={`symbol-search-class-${row.symbol}`}
                >
                  {CLASS_LABELS[row.asset_class] || row.asset_class}
                </span>
              </div>
            ))
          )}
        </div>
        <div className="border-t border-[var(--aurion-border)] px-3 py-2 text-[10px] text-[var(--aurion-muted)]">
          {filtered.length} of {items.length} symbols · press{" "}
          <span className="kbd">/</span> anywhere to reopen
        </div>
      </div>
    </div>
  );
}
